import { createStore } from '@/lib/storeCreator';
import type { LoginCredentials, User } from '@/features/auth/types';
import { authApi } from '@/features/auth/services';

export interface ActionResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}
import { getErrorMessage } from '@/types/api';
import { t } from 'i18next';

interface AuthStoreState {
  user: User | null;
  isAuthenticated: boolean;
  error: string | null;

  // Unified loading states using state machine pattern
  loading: {
    auth: 'idle' | 'logging-in' | 'logging-out' | 'checking' | 'error';
  };
}

interface AuthStoreActions {
  login: (credentials: LoginCredentials) => Promise<ActionResult<User>>;
  logout: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<boolean>;

  // Legacy compatibility helper (computed property)
  isLoading: () => boolean;
}

export const useAuthStore = createStore<AuthStoreState, AuthStoreActions>(
  {
    state: {
      user: null,
      isAuthenticated: false,
      error: null,
      loading: {
        auth: 'idle',
      },
    },
    actions: (set, get) => ({
      // Auth actions - NO try/catch needed, centralized API handles errors
      login: async (credentials: LoginCredentials) => {
        set(state => {
          state.loading.auth = 'logging-in';
          state.error = null;
        });

        const response = await authApi.login(credentials);

        // Check for 403 - No permission error
        if (response.error?.status === 403) {
          const errorMessage = t(
            'no_permission_error',
            'You do not have permission to access this system'
          );
          set(state => {
            state.error = errorMessage;
            state.loading.auth = 'error';
          });
          return { success: false, error: errorMessage };
        }

        if (
          response.error ||
          !response.isSuccess ||
          !response.result ||
          !response.result.success
        ) {
          const errorMessage = t('login_failed', 'Invalid login or password');
          set(state => {
            state.error = errorMessage;
            state.loading.auth = 'error';
          });
          return { success: false, error: errorMessage };
        }

        // Success case
        const data = response.result;
        const user: User = {
          name: data.userName || 'Unknown',
          userName: data.userName || 'Unknown',
          authenticatedAt: data.authenticatedAt || new Date().toISOString(),
          backOfficeAccessLevel: 1,
        };

        set(state => {
          state.user = user;
          state.isAuthenticated = true;
          state.loading.auth = 'idle';
          state.error = null;
        });
        return { success: true, data: user };
      },

      logout: async () => {
        set(state => {
          state.loading.auth = 'logging-out';
        });

        // Call logout endpoint to clear server-side cookie
        const response = await authApi.logout();

        // Don't fail logout if API call fails - clear client state regardless
        if (response.error) {
          getErrorMessage(response.error, true);
        }

        set(state => {
          state.user = null;
          state.isAuthenticated = false;
          state.error = null;
          state.loading.auth = 'idle';
        });
      },

      clearError: () => {
        set(state => {
          state.error = null;
        });
      },

      checkAuth: async () => {
        set(state => {
          state.loading.auth = 'checking';
        });

        // Check with server if cookie is still valid using /api/auth/status
        const response = await authApi.checkAuthStatus();

        if (response.error || !response.isSuccess) {
          // Not authenticated or error occurred
          set(state => {
            state.user = null;
            state.isAuthenticated = false;
            state.loading.auth = 'idle';
          });
          getErrorMessage(response.error, true);
          return false;
        }

        if (response.result?.data.isAuthenticated) {
          // Cookie is still valid, restore user state from server response
          const userData = response.result.data;
          const user: User = {
            name: userData.userName,
            userName: userData.userName,
            authenticatedAt: userData.authenticatedAt,
            backOfficeAccessLevel: 1,
          };

          set(state => {
            state.user = user;
            state.isAuthenticated = true;
            state.loading.auth = 'idle';
          });
          return true;
        } else {
          // Not authenticated
          set(state => {
            state.user = null;
            state.isAuthenticated = false;
            state.loading.auth = 'idle';
          });
          return false;
        }
      },

      // Legacy compatibility helper (computed property)
      isLoading: () => {
        const { loading } = get();
        return loading.auth !== 'idle';
      },
    }),
  },
  'AuthStore'
);
